import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface Props {
  open: boolean;
  title: string;
  /** Body copy — a string or richer node (e.g. dates emphasised inline). */
  description: React.ReactNode;
  /** Confirm button label. Defaults to "Confirm". */
  confirmLabel?: string;
  /** Cancel button label. Defaults to "Cancel". */
  cancelLabel?: string;
  /** Render the confirm action as destructive (red). */
  destructive?: boolean;
  /** Disable the confirm button, e.g. while the guarded mutation is in flight. */
  confirmDisabled?: boolean;
  /** The user confirmed — the caller proceeds with the guarded action. */
  onConfirm: () => void;
  /** The user backed out (button, ESC, or overlay click). */
  onCancel: () => void;
}

/**
 * Generic yes/no confirmation prompt. Rendered on top of any owning overlay;
 * ESC / overlay click resolve to Cancel (the safe choice), so only an explicit
 * confirm click proceeds. The caller owns the open state and both callbacks —
 * see {@link DiscardChangesDialog} for the unsaved-changes specialisation built
 * on the same dialog primitives.
 */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  confirmDisabled = false,
  onConfirm,
  onCancel,
}: Props): React.ReactElement {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? 'destructive' : 'default'}
            disabled={confirmDisabled}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
